/**
 * One show style (container, D32): read structurally, edited as JSON —
 * checked while typing with the engine's show-style schema
 * (`lib/showStyle/config.ts`) — saved with optimistic concurrency, and
 * deleted, which the server refuses while a studio names it as its default
 * (the message says which studios).
 *
 * Live and dirty-aware like the studio page (D35, `useLiveResource`): refreshed
 * in place without unsaved edits; with them, a banner offers "Overwrite with
 * mine" or "Discard mine and load theirs" (a 409 too); a delete is a banner.
 */
import type { ShowStyle } from '@acp/api-contract/showStyles.js';
import { ApiError } from '@acp/api-client/client.js';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ShowStyleConflict } from '../api/showStyles.js';
import { useDeleteShowStyle, useGrants, useLiveResource, useSaveShowStyle, useServices, useShowStyle } from '../app/hooks.js';
import { LiveUpdateBanner } from '../components/LiveUpdateBanner.js';
import { ShowStyleConfigEditor } from '../components/showStyles/ShowStyleConfigEditor.js';
import { ShowStyleSummary } from '../components/showStyles/ShowStyleSummary.js';
import { Button } from '../components/ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.js';
import { allows } from '../lib/permissions.js';
import { checkShowStyleText, showStyleSummary } from '../lib/showStyle/config.js';
import { type ConfigProblem, configText, serverProblems } from '../lib/studio/config.js';
import { whenText } from '../lib/live/whenText.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

type Draft = { readonly name: string; readonly text: string };

function draftOf(showStyle: ShowStyle): Draft {
  return { name: showStyle.name, text: configText(showStyle.config) };
}

function isDirty(draft: Draft, base: ShowStyle): boolean {
  return draft.text !== configText(base.config) || draft.name !== base.name;
}

export function ShowStyleView({ showStyleId }: { readonly showStyleId: string }) {
  const showStyle = useShowStyle(showStyleId);
  if (showStyle.data !== undefined) return <ShowStyleEditorPage key={showStyle.data.showStyleId} latest={showStyle.data} />;
  if (showStyle.error !== null) {
    return (
      <div className="mx-auto grid max-w-xl gap-4" role="alert" data-testid="show-style-failed">
        <p className="text-sm text-destructive">Could not load the show style: {messageOf(showStyle.error)}</p>
        <Button variant="outline" className="w-fit" onClick={() => void showStyle.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  return <p className="p-8 text-center text-sm text-muted-foreground">Loading the show style…</p>;
}

function ShowStyleEditorPage({ latest }: { readonly latest: ShowStyle }) {
  const grants = useGrants();
  const save = useSaveShowStyle();
  const remove = useDeleteShowStyle();
  const navigate = useNavigate();
  const { nowMs } = useServices();
  const [loaded] = useState(latest);
  const [draft, setDraft] = useState<Draft>(() => draftOf(loaded));
  const [serverIssues, setServerIssues] = useState<readonly ConfigProblem[] | null>(null);
  const live = useLiveResource<ShowStyle>({
    kind: 'showStyle',
    id: loaded.showStyleId,
    loaded,
    latest,
    isDirty: (base) => isDirty(draft, base),
    versionOf: (s) => s.version,
    authorOf: (s) => s.updatedBy,
    writtenAtOf: (s) => Date.parse(s.updatedAt),
    onReset: (s) => setDraft(draftOf(s)),
  });
  const base = live.base;
  const check = useMemo(() => checkShowStyleText(draft.text), [draft.text]);
  const savedCheck = useMemo(() => checkShowStyleText(configText(base.config)), [base]);
  const canSave = allows(grants, 'device.configure', { siteId: null, studioId: null });
  const dirty = isDirty(draft, base);
  const problems = check.ok ? (serverIssues ?? []) : check.problems;
  const summary = check.ok ? showStyleSummary(check.config) : savedCheck.ok ? showStyleSummary(savedCheck.config) : null;

  const submit = (version: number): void => {
    if (!check.ok) return;
    setServerIssues(null);
    live.beginSave();
    save.mutate(
      { showStyleId: base.showStyleId, version, name: draft.name.trim(), config: check.document },
      {
        onSuccess: (saved) => {
          live.saved(saved);
          toast.success(`Saved version ${String(saved.version)}`);
        },
        onError: (err) => {
          if (err instanceof ShowStyleConflict) live.conflict(err.current);
          else {
            live.saveFailed();
            if (err instanceof ApiError && err.code === 'validation-failed') setServerIssues(serverProblems(err.issues));
            else toast.error(`Not saved: ${messageOf(err)}`);
          }
        },
      },
    );
  };

  return (
    <div className="mx-auto grid max-w-6xl gap-6" data-testid="show-style-page">
      {live.notice === null ? null : (
        <LiveUpdateBanner
          notice={{ kind: live.notice.kind, who: live.notice.by, at: whenText(live.notice.atMs, nowMs()), what: 'show style' }}
          pending={save.isPending}
          canSave={canSave && check.ok}
          onOverwrite={() => {
            const theirs = live.keepMine();
            if (theirs !== null) submit(theirs.version);
          }}
          onDiscard={() => {
            setServerIssues(null);
            live.discardMine();
          }}
        />
      )}
      <Card className="relative" data-testid="show-style-card" data-refreshes={live.refreshes}>
        {live.refreshes > 0 ? <span key={live.refreshes} aria-hidden="true" className="pointer-events-none absolute inset-0 animate-live-flash rounded-xl ring-2 ring-success" data-testid="live-flash" /> : null}
        <CardHeader>
          <CardTitle>{base.name}</CardTitle>
          <CardDescription>
            <span className="font-mono">{base.showStyleId}</span> · version {base.version} · last written by {base.updatedBy}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="json" data-testid="show-style-json-tab">
                Config (JSON)
              </TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="pt-4">
              {summary !== null ? <ShowStyleSummary summary={summary} /> : <p className="text-sm text-muted-foreground">The stored config does not parse with this build’s schema; see the JSON.</p>}
            </TabsContent>
            <TabsContent value="json" className="pt-4">
              <ShowStyleConfigEditor
                name={draft.name}
                text={draft.text}
                problems={problems}
                problemSource={check.ok && serverIssues !== null ? 'server' : 'editor'}
                version={base.version}
                dirty={dirty}
                canSave={canSave}
                pending={save.isPending || remove.isPending}
                deleteError={remove.error === null ? null : messageOf(remove.error)}
                onName={(name) => setDraft({ ...draft, name })}
                onText={(text) => {
                  setServerIssues(null);
                  setDraft({ ...draft, text });
                }}
                onSave={() => submit(base.version)}
                onRevert={() => {
                  setServerIssues(null);
                  setDraft(draftOf(base));
                }}
                onDelete={() => {
                  remove.mutate(
                    { showStyleId: base.showStyleId, version: base.version },
                    {
                      onSuccess: (deleted) => {
                        toast.success(`Deleted “${deleted.name}”`);
                        void navigate({ to: '/show-styles' });
                      },
                    },
                  );
                }}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
